import { CardElement, ColorRank, GameCard } from "../types/battle"
import { RANK_ORDER } from "../constants/game.constants"
import {
  MailContract,
  MailContractStatus,
  MailContractActionResult,
  MailContractsMineResponse,
  PostOfficePoint,
  MailClaimContractResponse,
} from "../types/mail"
import { normalizeGameCard } from "./gameCards"

type RawRecord = Record<string, unknown>

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const COLOR_RANKS: ColorRank[] = [...RANK_ORDER]

const MAIL_STATUSES: MailContractStatus[] = [
  "OPEN",
  "IN_TRANSIT",
  "READY_TO_CLAIM",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
]

const DEFAULT_RADIUS_M = 50
const DEFAULT_MAIL_LIMIT = 3
const OPEN_LOT_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** ISO для OPEN-лота (7 дней) — бэкенд не должен вызывать toISOString на строке. */
export function mailOpenLotExpiresAt(now = Date.now()): string {
  return new Date(now + OPEN_LOT_TTL_MS).toISOString()
}

function pickIsoDate(value: unknown): string | null {
  if (value == null || value === "") return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    const date = new Date(trimmed)
    return Number.isNaN(date.getTime()) ? trimmed : date.toISOString()
  }
  return null
}

export const POSTAGE_RANK_SURCHARGE: Record<ColorRank, number> = {
  WHITE: 10,
  GREEN: 50,
  BLUE: 100,
  PURPLE: 200,
  ORANGE: 300,
  RED: 400,
}

export function computePostageFee(rank: ColorRank): number {
  return 25 + (POSTAGE_RANK_SURCHARGE[rank] ?? 10)
}

export function computePostageFeeForTrade(
  offeredRank: ColorRank,
  counterRank: ColorRank
): number {
  const higher =
    rankIndex(offeredRank) >= rankIndex(counterRank) ? offeredRank : counterRank
  return computePostageFee(higher)
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  const normalized = pickString(value).toUpperCase()
  return allowed.includes(normalized as T) ? (normalized as T) : fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function rankIndex(rank: ColorRank): number {
  const idx = RANK_ORDER.indexOf(rank)
  return idx >= 0 ? idx : 0
}

export function rankDiff(a: ColorRank, b: ColorRank): number {
  return Math.abs(rankIndex(a) - rankIndex(b))
}

export function isRankAtLeast(cardRank: ColorRank, minRank: ColorRank): boolean {
  return rankIndex(cardRank) >= rankIndex(minRank)
}

export function isSymmetricRankAllowed(
  offeredRank: ColorRank,
  counterRank: ColorRank
): boolean {
  return rankDiff(offeredRank, counterRank) <= 1
}

export function getHighestOwnedRank(cards: GameCard[]): ColorRank {
  let highest = 0
  for (const card of cards) {
    highest = Math.max(highest, rankIndex(card.colorRank))
  }
  return RANK_ORDER[highest] ?? "WHITE"
}

export function formatMailStatus(status: MailContractStatus): string {
  switch (status) {
    case "OPEN":
      return "Ожидает обмена"
    case "IN_TRANSIT":
      return "В пути"
    case "READY_TO_CLAIM":
      return "Заберите на почте"
    case "COMPLETED":
      return "Завершён"
    case "CANCELLED":
      return "Отменён"
    case "EXPIRED":
      return "Истёк"
    default:
      return status
  }
}

export function isActiveMailContract(contract: MailContract): boolean {
  return (
    contract.status === "OPEN" ||
    contract.status === "IN_TRANSIT" ||
    contract.status === "READY_TO_CLAIM"
  )
}

export function collectMailEscrowedCardIds(contracts: MailContract[]): Set<string> {
  const ids = new Set<string>()
  for (const contract of contracts) {
    if (!isActiveMailContract(contract)) continue
    if (contract.offeredCardId) ids.add(contract.offeredCardId)
    if (contract.counterCardId) ids.add(contract.counterCardId)
  }
  return ids
}

export function filterCardsForMailOffer(
  cards: GameCard[],
  escrowedIds: Set<string>,
  activeCardId?: string | null
): GameCard[] {
  return cards.filter(
    (card) =>
      card.id &&
      card.storedInBank !== true &&
      card.listedOnMarket !== true &&
      card.id !== activeCardId &&
      !escrowedIds.has(card.id)
  )
}

export function excludeMailEscrowedCards(
  cards: GameCard[],
  escrowedIds: Set<string>
): GameCard[] {
  return cards.filter(
    (card) => card.id && card.storedInBank !== true && !escrowedIds.has(card.id)
  )
}

export function filterCardsForMailAccept(
  cards: GameCard[],
  contract: MailContract,
  escrowedIds: Set<string>,
  activeCardId?: string | null,
  highestOwnedRank?: ColorRank
): GameCard[] {
  const maxRank = highestOwnedRank ?? getHighestOwnedRank(cards)
  return filterCardsForMailOffer(cards, escrowedIds, activeCardId).filter(
    (card) =>
      card.element === contract.wantedElement &&
      isRankAtLeast(card.colorRank, contract.wantedColorRank) &&
      rankIndex(card.colorRank) <= rankIndex(maxRank) &&
      isSymmetricRankAllowed(
        contract.offeredCard?.colorRank ?? contract.wantedColorRank,
        card.colorRank
      )
  )
}

export function normalizePostOfficePoint(raw: unknown): PostOfficePoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.lat ?? item.latitude)
  const lng = pickNumber(item.lng ?? item.longitude ?? item.lon)
  if (id == null || lat == null || lng == null) return null
  return {
    id: String(id),
    name: pickString(item.name ?? item.title, "Почта"),
    lat,
    lng,
    radiusM:
      pickNumber(
        item.radiusM ?? item.radiusMeters ?? item.radius_meters ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
    cityId: pickNumber(item.cityId ?? item.city_id) ?? undefined,
  }
}

export function mapPostOfficePoints(data: unknown): PostOfficePoint[] {
  if (Array.isArray(data)) {
    return data
      .map(normalizePostOfficePoint)
      .filter((point): point is PostOfficePoint => point != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.points ?? obj.items ?? obj.postOffices ?? obj.post_offices
  if (Array.isArray(list)) {
    return list
      .map(normalizePostOfficePoint)
      .filter((point): point is PostOfficePoint => point != null)
  }
  const single = normalizePostOfficePoint(obj)
  return single ? [single] : []
}

export function normalizeMailContract(raw: unknown): MailContract | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.contractId ?? item.contract_id
  if (id == null) return null

  const offeredCard =
    normalizeGameCard(item.offeredCard ?? item.offered_card) ?? undefined
  const counterCard =
    normalizeGameCard(item.counterCard ?? item.counter_card) ?? undefined

  return {
    id: String(id),
    status: pickEnum(item.status, MAIL_STATUSES, "OPEN"),
    roomCode: pickString(item.roomCode ?? item.room_code),
    cityId: pickNumber(item.cityId ?? item.city_id) ?? undefined,
    pointId: pickString(item.pointId ?? item.point_id) || undefined,
    deliveryZone:
      pickEnum(
        item.deliveryZone ?? item.delivery_zone,
        ["LOCAL", "REGIONAL", "INTER_REGIONAL"] as const,
        "LOCAL"
      ) || null,
    creatorUserId: pickNumber(item.creatorUserId ?? item.creator_user_id),
    creatorPublicCode: pickString(
      item.creatorPublicCode ?? item.creator_public_code
    ) || null,
    creatorNickname:
      pickString(item.creatorNickname ?? item.creator_nickname) || null,
    offeredCardId:
      pickString(
        item.offeredCardId ??
          item.offered_card_id ??
          offeredCard?.id
      ) || null,
    offeredCard: offeredCard ?? null,
    wantedElement: pickEnum(
      item.wantedElement ?? item.wanted_element,
      CARD_ELEMENTS,
      "FIRE"
    ),
    wantedColorRank: pickEnum(
      item.wantedColorRank ?? item.wanted_color_rank,
      COLOR_RANKS,
      "WHITE"
    ),
    acceptorUserId: pickNumber(item.acceptorUserId ?? item.acceptor_user_id),
    acceptorPublicCode:
      pickString(item.acceptorPublicCode ?? item.acceptor_public_code) || null,
    acceptorNickname:
      pickString(item.acceptorNickname ?? item.acceptor_nickname) || null,
    counterCardId:
      pickString(
        item.counterCardId ?? item.counter_card_id ?? counterCard?.id
      ) || null,
    counterCard: counterCard ?? null,
    etaAt: pickIsoDate(item.etaAt ?? item.eta_at),
    expiresAt: pickIsoDate(item.expiresAt ?? item.expires_at),
    creatorClaimedAt: pickIsoDate(item.creatorClaimedAt ?? item.creator_claimed_at),
    acceptorClaimedAt: pickIsoDate(item.acceptorClaimedAt ?? item.acceptor_claimed_at),
    createdAt: pickIsoDate(item.createdAt ?? item.created_at),
    postageFeeCoins:
      pickNumber(item.postageFeeCoins ?? item.postage_fee_coins) ?? null,
  }
}

export function mapMailContracts(data: unknown): MailContract[] {
  if (Array.isArray(data)) {
    return data
      .map(normalizeMailContract)
      .filter((item): item is MailContract => item != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.contracts ?? obj.items ?? obj.lots ?? obj.openLots
  if (Array.isArray(list)) {
    return list
      .map(normalizeMailContract)
      .filter((item): item is MailContract => item != null)
  }
  const single = normalizeMailContract(obj)
  return single ? [single] : []
}

export function normalizeMailContractsMine(
  data: unknown
): MailContractsMineResponse {
  const obj = unwrapPayload(data)
  const contracts = mapMailContracts(obj.contracts ?? obj)
  const limit =
    pickNumber(obj.mailActionsLimit ?? obj.mail_actions_limit, DEFAULT_MAIL_LIMIT) ??
    DEFAULT_MAIL_LIMIT
  const today =
    pickNumber(obj.mailActionsToday ?? obj.mail_actions_today, 0) ?? 0
  const remaining =
    pickNumber(
      obj.mailActionsRemaining ?? obj.mail_actions_remaining,
      Math.max(0, limit - today)
    ) ?? Math.max(0, limit - today)

  return {
    contracts,
    mailActionsToday: today,
    mailActionsLimit: limit,
    mailActionsRemaining: remaining,
  }
}

export function normalizeMailClaimResponse(
  data: unknown
): MailClaimContractResponse | null {
  const obj = unwrapPayload(data)
  const contract = normalizeMailContract(obj.contract ?? obj)
  const receivedCard = normalizeGameCard(
    obj.receivedCard ?? obj.received_card ?? obj.card
  )
  if (!contract || !receivedCard) return null
  return { contract, receivedCard }
}

export function normalizeMailContractActionResult(
  data: unknown
): MailContractActionResult | null {
  const obj = unwrapPayload(data)
  const contract = normalizeMailContract(obj.contract ?? obj.lot ?? obj)
  if (!contract) return null
  return {
    contract,
    postageFeeCoins:
      pickNumber(obj.postageFeeCoins ?? obj.postage_fee_coins) ?? undefined,
    championFundCoins:
      pickNumber(obj.championFundCoins ?? obj.champion_fund_coins) ?? undefined,
    fundDelta: pickNumber(obj.fundDelta ?? obj.fund_delta) ?? undefined,
  }
}

export function formatDeliveryZoneLabel(
  zone: MailContract["deliveryZone"]
): string {
  switch (zone) {
    case "LOCAL":
      return "1–3 ч"
    case "REGIONAL":
      return "6–12 ч"
    case "INTER_REGIONAL":
      return "12–24 ч"
    default:
      return ""
  }
}

export function canCancelMailContract(
  contract: MailContract,
  userId?: number | null
): boolean {
  return (
    contract.status === "OPEN" &&
    userId != null &&
    contract.creatorUserId === userId
  )
}

export function canClaimMailContract(
  contract: MailContract,
  userId?: number | null
): boolean {
  if (contract.status !== "READY_TO_CLAIM" || userId == null) return false
  if (contract.creatorUserId === userId) {
    return !contract.creatorClaimedAt
  }
  if (contract.acceptorUserId === userId) {
    return !contract.acceptorClaimedAt
  }
  return false
}

export function canAcceptMailContract(contract: MailContract): boolean {
  return contract.status === "OPEN"
}

export const MAIL_ELEMENTS: CardElement[] = [...CARD_ELEMENTS]
export const MAIL_COLOR_RANKS: ColorRank[] = [...COLOR_RANKS]
